import React, { useEffect, useMemo, useState } from 'react';
import { Star, Plus, Search, RotateCcw, Layers, CheckCircle2, XCircle, FolderTree, ImagePlus, FileText, Banknote, Pencil, Power, Trash2, X, ListChecks, SlidersHorizontal, Clock, Eye, ArrowRight, Languages } from 'lucide-react';
import '../../components/dashboard/DashForm.css';
import './AdminOrders.css';
import '../../components/dashboard/AdminTranslatorFilters.css';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { adminApi } from '../../api/client';
import { formatEur, tndToEur } from '../../utils/currency';
import { mainServiceGroups, mainServices } from '../../data/mainServices';
import { filterTypesFor, TRANSLATION_SLUGS, DOCUMENT_SLUGS } from '../../data/serviceFilters';
import { interpretingServices } from '../../data/interpretingServices';
import StatusBadge from '../../components/dashboard/StatusBadge';
import assetUrl from '../../assetUrl';
import DashModal from '../../components/dashboard/DashModal';
import MessengerPanel from '../../components/dashboard/MessengerPanel';
import NotificationsPanel from '../../components/dashboard/NotificationsPanel';
import Pagination, { usePagination } from '../../components/dashboard/Pagination';
import { useCatalogLanguages } from '../../data/subServicesStore';
import { allCountries } from '../../data/quoteFormOptions';
import { flagEmoji, langFlagUrl } from '../../utils/langFlags';

export function AdminQuotesPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const orderFilter = searchParams.get('order');
  const orderFilterCode = searchParams.get('code');
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [quotes, setQuotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    estimatedPrice: '',
    deadline: '',
    adminNotes: '',
    send: true,
  });
  const [createForm, setCreateForm] = useState({
    clientId: '',
    orderId: '',
    type: 'Traduction',
    sourceLang: 'FR',
    targetLang: 'AR',
    estimatedPrice: '',
    details: { translation: '', proofreading: '', verification: '', delivery: '', urgency: '' },
    message: '',
    send: true,
  });
  const [msg, setMsg] = useState('');
  const [orders, setOrders] = useState([]);
  const { languages: catalogLangs } = useCatalogLanguages();
  const visibleQuotes = orderFilter ? quotes.filter((q) => String(q.orderId) === String(orderFilter)) : quotes;
  const quotesPg = usePagination(visibleQuotes, 6);

  const reload = () => adminApi.quotes().then((res) => setQuotes(res.quotes || [])).catch(() => setQuotes([]));

  useEffect(() => {
    reload();
    adminApi.users().then((res) => setUsers((res.users || []).filter((u) => u.role === 'client'))).catch(() => setUsers([]));
    adminApi.orders({ status: 'pending,request_sent' }).then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));
  }, []);

  const openEdit = (q) => {
    setEditing(q);
    setForm({
      estimatedPrice: q.estimatedPrice ?? '',
      deadline: q.deadline || '',
      adminNotes: q.adminNotes || '',
      send: true,
    });
  };

  const saveQuote = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setMsg('');
    try {
      await adminApi.updateQuote(editing.id, {
        estimatedPrice: form.estimatedPrice === '' ? undefined : Number(form.estimatedPrice),
        deadline: form.deadline || null,
        adminNotes: form.adminNotes,
        send: form.send,
      });
      setEditing(null);
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    }
  };

  const createQuote = async (e) => {
    e.preventDefault();
    setMsg('');
    const details = Object.fromEntries(
      Object.entries(createForm.details || {}).map(([k, v]) => [k, v === '' || v == null ? 0 : Number(v)])
    );
    const breakdownTotal = details.translation + details.proofreading + details.verification + details.delivery + details.urgency;
    const total = createForm.estimatedPrice === '' ? breakdownTotal : Number(createForm.estimatedPrice);
    try {
      await adminApi.createQuote({
        ...createForm,
        estimatedPrice: total || null,
        details,
      });
      setCreateOpen(false);
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.quotesTitle}</h1>
        <p>{orderFilter ? `Devis de la commande ${orderFilterCode || ''}` : a.quotesSubtitle}</p>
      </header>
      <div className="dash-actions">
        <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>{a.createQuote}</button>
        {orderFilter && (
          <Link className="btn btn-outline" to="/admin/quotes">Tous les devis</Link>
        )}
      </div>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>{a.colCode || '#'}</th>
                <th>{a.colClient}</th>
                <th>{a.colLangs}</th>
                <th>{a.colTotal}</th>
                <th>{a.colStatus}</th>
                <th>{d.actions}</th>
              </tr>
            </thead>
            <tbody>
              {quotesPg.pageItems.map((q) => (
                <tr key={q.id}>
                  <td>{q.id}</td>
                  <td>{q.client || q.clientEmail || '—'}</td>
                  <td>{q.langs}</td>
                  <td>{q.estimatedPrice != null ? `${q.estimatedPrice} TND` : '—'}</td>
                  <td><StatusBadge status={q.status} labels={d.status} /></td>
                  <td>
                    <button type="button" className="dash-btn-sm" onClick={() => openEdit(q)}>{a.answerQuote}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={quotesPg.page} totalPages={quotesPg.totalPages} total={quotesPg.total} onChange={quotesPg.setPage} />
      </section>

      <DashModal open={Boolean(editing)} title={a.answerQuote} onClose={() => setEditing(null)}>
        <form className="dash-form" onSubmit={saveQuote} style={{ maxWidth: '100%' }}>
          <div className="dash-field">
            <label>{a.colTotal} (TND)</label>
            <input type="number" value={form.estimatedPrice} onChange={(e) => setForm({ ...form, estimatedPrice: e.target.value })} required />
          </div>
          <div className="dash-field">
            <label>{a.deadline}</label>
            <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
          </div>
          <div className="dash-field">
            <label>{a.adminNotes}</label>
            <textarea rows={3} value={form.adminNotes} onChange={(e) => setForm({ ...form, adminNotes: e.target.value })} />
          </div>
          <label className="dash-check">
            <input type="checkbox" checked={form.send} onChange={(e) => setForm({ ...form, send: e.target.checked })} />
            {a.sendQuote}
          </label>
          <button type="submit" className="btn btn-primary">{d.save}</button>
        </form>
      </DashModal>

      <DashModal open={createOpen} title={a.createQuote} onClose={() => setCreateOpen(false)}>
        <form className="dash-form" onSubmit={createQuote} style={{ maxWidth: '100%' }}>
          <div className="dash-field">
            <label>{a.colClient}</label>
            <select required value={createForm.clientId} onChange={(e) => setCreateForm({ ...createForm, clientId: e.target.value })}>
              <option value="">—</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
              ))}
            </select>
          </div>
          <div className="dash-field">
            <label>Commande liée (optionnel)</label>
            <select value={createForm.orderId} onChange={(e) => setCreateForm({ ...createForm, orderId: e.target.value })}>
              <option value="">—</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>{o.code} — {o.document}</option>
              ))}
            </select>
          </div>
          <div className="dash-field">
            <label>{a.serviceType}</label>
            <input value={createForm.type} onChange={(e) => setCreateForm({ ...createForm, type: e.target.value })} />
          </div>
          <div className="dash-field">
            <label>{d.fromLang} / {d.toLang}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {catalogLangs.length ? (
                <>
                  <select value={createForm.sourceLang} onChange={(e) => setCreateForm({ ...createForm, sourceLang: e.target.value })}>
                    {catalogLangs.map((l) => <option key={`s-${l.code}`} value={l.code}>{[flagEmoji(l.flag), l.code].filter(Boolean).join(' ')}</option>)}
                  </select>
                  <select value={createForm.targetLang} onChange={(e) => setCreateForm({ ...createForm, targetLang: e.target.value })}>
                    {catalogLangs.map((l) => <option key={`t-${l.code}`} value={l.code}>{[flagEmoji(l.flag), l.code].filter(Boolean).join(' ')}</option>)}
                  </select>
                </>
              ) : (
                <>
                  <input value={createForm.sourceLang} onChange={(e) => setCreateForm({ ...createForm, sourceLang: e.target.value })} />
                  <input value={createForm.targetLang} onChange={(e) => setCreateForm({ ...createForm, targetLang: e.target.value })} />
                </>
              )}
            </div>
          </div>
          <div className="dash-field">
            <label>Détail du devis (TND)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                ['translation', 'Traduction'],
                ['proofreading', 'Relecture'],
                ['verification', 'Vérification'],
                ['delivery', 'Livraison'],
                ['urgency', 'Urgence'],
              ].map(([key, label]) => (
                <input
                  key={key}
                  type="number"
                  placeholder={label}
                  value={createForm.details[key]}
                  onChange={(e) => setCreateForm({ ...createForm, details: { ...createForm.details, [key]: e.target.value } })}
                />
              ))}
            </div>
          </div>
          <div className="dash-field">
            <label>{a.colTotal} (TND)</label>
            <input type="number" value={createForm.estimatedPrice} onChange={(e) => setCreateForm({ ...createForm, estimatedPrice: e.target.value })} placeholder="Total ou somme auto" />
          </div>
          <div className="dash-field">
            <label>{a.message}</label>
            <textarea rows={2} value={createForm.message} onChange={(e) => setCreateForm({ ...createForm, message: e.target.value })} />
          </div>
          <label className="dash-check">
            <input type="checkbox" checked={createForm.send} onChange={(e) => setCreateForm({ ...createForm, send: e.target.checked })} />
            {a.sendQuote}
          </label>
          <button type="submit" className="btn btn-primary">{d.save}</button>
        </form>
      </DashModal>
    </>
  );
}

const EMPTY_LANG_FORM = {
  code: '', nameFr: '', nameEn: '', nameAr: '', nativeName: '', flag: '', flagUrl: '', isActive: true,
};

export function AdminLanguagesPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [statFilter, setStatFilter] = useState('all');
  const [form, setForm] = useState(EMPTY_LANG_FORM);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmLang, setConfirmLang] = useState(null);
  const [toggleLang, setToggleLang] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const reload = () => adminApi.languages().then((res) => setItems(res.languages || [])).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);

  // Le toast s'affiche quelques secondes puis disparaît
  useEffect(() => {
    if (!msg) return undefined;
    const timer = setTimeout(() => setMsg(''), 3500);
    return () => clearTimeout(timer);
  }, [msg]);

  const q = search.trim().toLowerCase();
  const filtered = items
    .filter((l) => (statFilter === 'active' ? l.isActive : statFilter === 'inactive' ? !l.isActive : true))
    .filter((l) => !q || `${l.code} ${l.nameFr} ${l.nameEn} ${l.nativeName}`.toLowerCase().includes(q));

  const { page, totalPages, pageItems, setPage, total } = usePagination(filtered, 15);

  const langStats = [
    { id: 'all', icon: Languages, label: 'Toutes', value: items.length },
    { id: 'active', icon: CheckCircle2, label: 'Actives', value: items.filter((l) => l.isActive).length },
    { id: 'inactive', icon: XCircle, label: 'Inactives', value: items.filter((l) => !l.isActive).length },
  ];

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_LANG_FORM);
    setFormOpen(true);
  };

  const openEdit = (lang) => {
    setEditing(lang);
    setForm({
      code: lang.code,
      nameFr: lang.nameFr || '',
      nameEn: lang.nameEn || '',
      nameAr: lang.nameAr || '',
      nativeName: lang.nativeName || '',
      flag: lang.flag || '',
      flagUrl: lang.flagUrl || '',
      isActive: lang.isActive !== false,
    });
    setFormOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, flagUrl: previewUrl };
      if (editing) await adminApi.updateLanguage(editing.id, payload);
      else await adminApi.createLanguage(payload);
      setFormOpen(false);
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    } finally {
      setBusy(false);
    }
  };

  // Activation et désactivation passent toutes deux par le popup de confirmation.
  const doToggle = async () => {
    if (!toggleLang) return;
    setBusy(true);
    try {
      await adminApi.updateLanguage(toggleLang.id, { isActive: !toggleLang.isActive });
      setToggleLang(null);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirmLang) return;
    setBusy(true);
    try {
      await adminApi.deleteLanguage(confirmLang.id);
      setConfirmLang(null);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = langFlagUrl({ flag: form.flag, flagUrl: form.flagUrl });

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><Languages size={22} /></span>
          <div className="orders-hero-text" style={{ flex: 1 }}>
            <h1>{a.languagesTitle}</h1>
            <p>{a.languagesSubtitle}</p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
            onClick={openCreate}
          >
            <Plus size={16} />
            {a.addLanguage}
          </button>
        </div>
        <div className="orders-hero-stats" role="tablist">
          {langStats.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={statFilter === s.id}
              className={`orders-stat${statFilter === s.id ? ' is-active' : ''}`}
              onClick={() => setStatFilter(s.id)}
            >
              <span className="orders-stat-icon"><s.icon size={15} /></span>
              <span className="orders-stat-body">
                <strong>{s.value}</strong>
                <em>{s.label}</em>
              </span>
            </button>
          ))}
        </div>
      </header>
      {msg && (
        <div className="dash-toast dash-toast--float dash-toast--success" role="status">
          <CheckCircle2 size={16} aria-hidden="true" />
          <span>{msg}</span>
          <button type="button" onClick={() => setMsg('')} aria-label="Fermer">
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />Filtres</span>
          <span className="of-meta">
            <span>{filtered.length} / {items.length}</span>
            <button type="button" className="of-reset" onClick={() => { setSearch(''); setStatFilter('all'); }}>
              <RotateCcw size={13} />Réinitialiser
            </button>
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>Recherche</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Code, nom FR/EN ou natif…"
            />
          </div>
          <div className="of-field">
            <label>Statut</label>
            <select value={statFilter} onChange={(e) => setStatFilter(e.target.value)}>
              <option value="all">Tous</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
        </div>
      </div>

      <section className="dash-panel" style={{ marginTop: 16 }}>
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Drapeau</th>
                <th>Code</th>
                <th>FR</th>
                <th>Natif</th>
                <th>{d.status.active}</th>
                <th>{d.actions}</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((lang) => (
                <tr key={lang.id}>
                  <td>
                    {langFlagUrl(lang) ? (
                      <img src={langFlagUrl(lang)} alt="" width={24} style={{ borderRadius: 3, border: '1px solid rgba(0,0,0,.08)' }} />
                    ) : (flagEmoji(lang.flag) || '—')}
                  </td>
                  <td>{lang.code}</td>
                  <td>{lang.nameFr}</td>
                  <td>{lang.nativeName || '—'}</td>
                  <td>
                    <span
                      className="dash-btn-sm"
                      style={{
                        pointerEvents: 'none',
                        color: lang.isActive ? '#15803d' : '#b91c1c',
                        background: lang.isActive ? 'rgba(21,128,61,.08)' : 'rgba(185,28,28,.08)',
                      }}
                    >
                      {lang.isActive ? d.status.active : 'Inactif'}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="dash-btn-sm"
                      onClick={() => openEdit(lang)}
                    >
                      {d.edit}
                    </button>
                    <button
                      type="button"
                      className="dash-btn-sm"
                      style={{ marginInlineStart: 6 }}
                      onClick={() => setToggleLang(lang)}
                    >
                      {lang.isActive ? a.deactivate : a.activate}
                    </button>
                    <button
                      type="button"
                      className="dash-btn-sm"
                      style={{ marginInlineStart: 6 }}
                      onClick={() => setConfirmLang(lang)}
                    >
                      {a.delete}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} />
      </section>

      <DashModal
        open={formOpen}
        title={editing ? `${d.edit} — ${editing.code}` : a.addLanguage}
        subtitle="Code ISO, noms affichés et drapeau représentatif."
        onClose={() => setFormOpen(false)}
      >
        <form className="dash-form" onSubmit={onSubmit}>
          <div className="dform-section" style={{ marginBottom: 14 }}>
            <header className="dform-section-head" style={{ marginBottom: 10 }}>
              <Languages size={16} />
              <h4>Langue</h4>
            </header>
            <div className="dform-grid dform-grid--2">
              <div className="dform-field">
                <label>Code ISO <span className="dform-req">*</span></label>
                <input
                  required
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="FR"
                  maxLength={3}
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              <div className="dform-field">
                <label>Nom natif</label>
                <input value={form.nativeName} onChange={(e) => setForm({ ...form, nativeName: e.target.value })} placeholder="Français" />
              </div>
              <div className="dform-field">
                <label>Nom (FR) <span className="dform-req">*</span></label>
                <input required value={form.nameFr} onChange={(e) => setForm({ ...form, nameFr: e.target.value })} placeholder="Français" />
              </div>
              <div className="dform-field">
                <label>Nom (EN)</label>
                <input value={form.nameEn} onChange={(e) => setForm({ ...form, nameEn: e.target.value })} placeholder="French" />
              </div>
              <div className="dform-field dform-wide">
                <label>Nom (AR)</label>
                <input value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} placeholder="الفرنسية" dir="rtl" />
              </div>
            </div>
          </div>

          <div className="dform-section" style={{ marginBottom: 14 }}>
            <header className="dform-section-head" style={{ marginBottom: 10 }}>
              <ImagePlus size={16} />
              <h4>Drapeau</h4>
            </header>
            <div style={{ display: 'flex', gap: 14, alignItems: 'stretch' }}>
              <div
                style={{
                  width: 96,
                  minHeight: 72,
                  borderRadius: 10,
                  border: '1px solid rgba(0,0,0,.1)',
                  background: 'rgba(0,0,0,.03)',
                  display: 'grid',
                  placeItems: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                  fontSize: 34,
                }}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  flagEmoji(form.flag) || '🌐'
                )}
              </div>
              <div className="dform-grid" style={{ flex: 1, gap: 10 }}>
                <div className="dform-field">
                  <label>Pays représentatif</label>
                  <select value={form.flag} onChange={(e) => setForm({ ...form, flag: e.target.value, flagUrl: '' })}>
                    <option value="">— Aucun / personnalisé —</option>
                    {allCountries.filter((c) => c.code !== 'other').map(({ code, name }) => (
                      <option key={code} value={code}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="dform-field">
                  <label>URL personnalisée (optionnel)</label>
                  <input
                    value={form.flagUrl}
                    onChange={(e) => setForm({ ...form, flagUrl: e.target.value })}
                    placeholder="https://flagcdn.com/fr.svg"
                  />
                </div>
              </div>
            </div>
          </div>

          <label className="dform-checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Langue active sur la plateforme
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? '…' : d.save}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setFormOpen(false)}>
              {d.cancel}
            </button>
          </div>
        </form>
      </DashModal>

      <DashModal
        open={Boolean(toggleLang)}
        title={toggleLang?.isActive ? 'Désactiver la langue' : 'Activer la langue'}
        subtitle={toggleLang ? `${toggleLang.nameFr} (${toggleLang.code})` : ''}
        onClose={() => setToggleLang(null)}
      >
        {toggleLang && (
          <div style={{ padding: '4px 2px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                marginBottom: 14,
                borderRadius: 10,
                border: '1px solid rgba(0,0,0,.08)',
                background: 'rgba(0,0,0,.02)',
              }}
            >
              {langFlagUrl(toggleLang) ? (
                <img
                  src={langFlagUrl(toggleLang)}
                  alt=""
                  width={40}
                  style={{ borderRadius: 6, border: '1px solid rgba(0,0,0,.1)', flexShrink: 0 }}
                />
              ) : (
                <span style={{ fontSize: 28, lineHeight: 1 }}>{flagEmoji(toggleLang.flag) || '🌐'}</span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: 'block' }}>{toggleLang.nameFr}</strong>
                <span style={{ fontSize: '.8rem', opacity: .6 }}>
                  {toggleLang.code}{toggleLang.nativeName ? ` · ${toggleLang.nativeName}` : ''}
                </span>
              </div>
              <span
                className="dash-btn-sm"
                style={{
                  pointerEvents: 'none',
                  color: toggleLang.isActive ? '#15803d' : '#b91c1c',
                  background: toggleLang.isActive ? 'rgba(21,128,61,.08)' : 'rgba(185,28,28,.08)',
                }}
              >
                {toggleLang.isActive ? d.status.active : 'Inactif'}
              </span>
            </div>
            <p style={{ margin: '0 0 18px', fontSize: '.92rem', lineHeight: 1.6 }}>
              {toggleLang.isActive
                ? 'Cette langue ne sera plus proposée aux clients et aux traducteurs.'
                : 'Cette langue sera de nouveau proposée aux clients et aux traducteurs.'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-outline" onClick={() => setToggleLang(null)} disabled={busy}>
                {d.cancel}
              </button>
              <button
                type="button"
                className={`btn ${toggleLang.isActive ? 'btn-danger' : 'btn-primary'}`}
                onClick={doToggle}
                disabled={busy}
              >
                {busy ? '…' : (toggleLang.isActive ? a.deactivate : a.activate)}
              </button>
            </div>
          </div>
        )}
      </DashModal>

      <DashModal
        open={Boolean(confirmLang)}
        title="Supprimer la langue"
        subtitle={confirmLang ? `${confirmLang.nameFr} (${confirmLang.code})` : ''}
        onClose={() => setConfirmLang(null)}
      >
        {confirmLang && (
          <div style={{ padding: '4px 2px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                marginBottom: 14,
                borderRadius: 10,
                border: '1px solid rgba(185,28,28,.18)',
                background: 'rgba(185,28,28,.04)',
              }}
            >
              {langFlagUrl(confirmLang) ? (
                <img
                  src={langFlagUrl(confirmLang)}
                  alt=""
                  width={40}
                  style={{ borderRadius: 6, border: '1px solid rgba(0,0,0,.1)', flexShrink: 0 }}
                />
              ) : (
                <span style={{ fontSize: 28, lineHeight: 1 }}>{flagEmoji(confirmLang.flag) || '🌐'}</span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: 'block' }}>{confirmLang.nameFr}</strong>
                <span style={{ fontSize: '.8rem', opacity: .6 }}>
                  {confirmLang.code}{confirmLang.nativeName ? ` · ${confirmLang.nativeName}` : ''}
                </span>
              </div>
              <Trash2 size={18} style={{ color: '#b91c1c', flexShrink: 0 }} />
            </div>
            <p style={{ margin: '0 0 18px', fontSize: '.92rem', lineHeight: 1.6 }}>
              Supprimer définitivement cette langue du catalogue ? Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-outline" onClick={() => setConfirmLang(null)} disabled={busy}>
                {d.cancel}
              </button>
              <button type="button" className="btn btn-danger" onClick={doDelete} disabled={busy}>
                {busy ? '…' : a.delete}
              </button>
            </div>
          </div>
        )}
      </DashModal>
    </>
  );
}

// Services principaux fixes (config/services.json) + parents supplémentaires
// utilisés par le formulaire de commande mais absents des pages /services/type/*
const EXTRA_PARENTS = [
  { slug: 'interpreting', name: 'Interprétariat (dynamique)' },
  { slug: 'it', name: 'Informatique (domaine commande)' },
  { slug: 'scientific', name: 'Scientifique (domaine commande)' },
  { slug: 'education', name: 'Éducation (domaine commande)' },
  { slug: 'other', name: 'Autre' },
  { slug: 'certificationDocs', name: 'Certification de documents (commande)' },
];

const parentLabel = (slug) =>
  mainServices.find((s) => s.slug === slug)?.name
  || EXTRA_PARENTS.find((e) => e.slug === slug)?.name
  || slug || '—';

const EMPTY_SERVICE = {
  titleFr: '', titleEn: '', titleAr: '', type: '', tags: [], options: [], slug: '', isActive: true,
  description: '', image: '', quote: false, featured: false,
  prices: { document: '', page: '', word: '', hour: '', service: '' },
  prestation: 'translation', interpChoice: '',
};

// Section « Options disponibles » — selon la prestation
const TRANSLATION_OPTIONS = [
  ['certification', 'Certification'],
  ['assermentation', 'Assermentation'],
  ['legalisation', 'Légalisation'],
  ['apostille', 'Apostille'],
  ['avocat', 'Vérification par avocat'],
  ['notaire', 'Vérification par notaire'],
  ['express', 'Livraison express'],
];

const INTERPRETING_OPTIONS = [
  ['presentiel', 'Présentiel'],
  ['visio', 'Visioconférence'],
  ['telephone', 'Téléphone'],
];

// Prix par unité — un champ par mode de tarification
const PRICE_FIELDS = [
  ['document', 'Prix par document'],
  ['page', 'Prix par page'],
  ['word', 'Prix par mot'],
  ['hour', 'Prix par heure'],
  ['service', 'Forfait'],
];

// Options « Type de traduction » / « Type de document » (cases à cocher du formulaire)
const TRANSLATION_TYPE_OPTIONS = mainServiceGroups.filter((g) => g.id !== 'documents').flatMap((g) => g.services);
const DOCUMENT_TYPE_OPTIONS = mainServiceGroups.find((g) => g.id === 'documents')?.services || [];

const UNIT_LABELS = { document: '/doc', page: '/page', word: '/mot', hour: '/h', service: '' };

// « Général » — option d'interprétariat hors config (supprimée du interpreting.json)
const GENERAL_INTERPRETING = {
  slug: 'generalInterpreting',
  name: 'Interprétariat général',
  nameEn: 'General interpreting',
  nameAr: 'الترجمة الشفوية العامة',
  image: '/images/interpreting-general.jpg',
};

const slugify = (str) => (str || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

// Prix par unité d'un enregistrement DB → état du formulaire (avec fallback basePrice/unit)
const pricesForForm = (s) => {
  const pr = s.prices || {};
  const out = { document: '', page: '', word: '', hour: '', service: '' };
  for (const k of Object.keys(out)) if (pr[k] > 0) out[k] = pr[k];
  if (!Object.values(out).some(Boolean) && (s.basePrice || 0) > 0 && s.unit && s.unit in out) {
    out[s.unit] = s.basePrice;
  }
  return out;
};

// Liste des prix définis pour l'affichage de la card admin
const priceEntries = (s) => {
  const entries = Object.entries(s.prices || {}).filter(([, v]) => Number(v) > 0);
  if (!entries.length && (s.basePrice || 0) > 0) entries.push([s.unit || '', s.basePrice]);
  return entries;
};

export function AdminServicesPage() {
  const { t, lang } = useLanguage();
  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-GB' : 'fr-FR';
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [items, setItems] = useState([]);
  const [serviceForm, setServiceForm] = useState(EMPTY_SERVICE);
  const [editingService, setEditingService] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const [imgPreview, setImgPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [prestation, setPrestation] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [docFilter, setDocFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [msg, setMsg] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // { kind: 'toggle'|'delete', service }

  const reload = () => {
    adminApi.services().then((res) => setItems(res.services || [])).catch(() => setItems([]));
  };
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(() => setMsg(''), 4500);
    return () => clearTimeout(t);
  }, [msg]);

  const norm = (s) => (s || '').toLowerCase();
  const visibleServices = items.filter((s) => {
    const isInterp = s.category === 'interpreting' || s.type === 'interpreting';
    if (prestation !== 'all' && isInterp !== (prestation === 'interpreting')) return false;
    if (prestation === 'interpreting' && serviceFilter !== 'all') {
      const slug = norm(s.slug);
      if (serviceFilter === 'other') {
        if (interpretingServices.some((i) => norm(i.slug) === slug)) return false;
      } else if (slug !== norm(serviceFilter)) return false;
    }
    if (prestation === 'translation' && serviceFilter !== 'all') {
      const fts = filterTypesFor(s);
      if (serviceFilter === 'other') {
        if (fts.some((f) => TRANSLATION_SLUGS.has(f))) return false;
      } else if (!fts.includes(serviceFilter.toLowerCase())) return false;
    }
    if (prestation === 'translation' && docFilter !== 'all') {
      const fts = filterTypesFor(s);
      if (docFilter === 'other') {
        if (fts.some((f) => DOCUMENT_SLUGS.has(f))) return false;
      } else if (!fts.includes(docFilter.toLowerCase())) return false;
    }
    if (statusFilter !== 'all' && Boolean(s.isActive) !== (statusFilter === 'active')) return false;
    const q = norm(search).trim();
    if (q && !norm(`${s.slug} ${s.titleFr} ${s.titleEn} ${s.titleAr}`).includes(q)) return false;
    return true;
  });

  const pg = usePagination(visibleServices, 12);

  const onPrestationChange = (value) => {
    setServiceForm((f) => ({ ...f, prestation: value, type: '', tags: [], options: [], interpChoice: '' }));
  };

  // Cases « Options supplémentaires » — multi-sélection
  const toggleOption = (slug) => {
    setServiceForm((f) => ({
      ...f,
      options: f.options.includes(slug)
        ? f.options.filter((o) => o !== slug)
        : [...f.options, slug],
    }));
  };

  // Cases à cocher « Type de traduction » / « Type de document » — multi-sélection
  const toggleTag = (slug) => {
    setServiceForm((f) => {
      const lcSlug = slug.toLowerCase();
      const tags = f.tags.some((t) => t.toLowerCase() === lcSlug)
        ? f.tags.filter((t) => t.toLowerCase() !== lcSlug)
        : [...f.tags, slug];
      const type = tags.find((t) => TRANSLATION_SLUGS.has(t.toLowerCase()))
        || tags.find((t) => DOCUMENT_SLUGS.has(t.toLowerCase()))
        || (tags.some((t) => t === 'other' || t === 'otherdoc') ? 'other' : '');
      return { ...f, tags, type };
    });
  };

  const onServicePrincipalChange = (value) => {
    const conf = value === 'general'
      ? GENERAL_INTERPRETING
      : interpretingServices.find((i) => i.slug === value) || null;
    setServiceForm((f) => ({
      ...f,
      interpChoice: value,
      type: 'interpreting',
      tags: value === 'other' ? [] : [value],
      ...(conf && !slugEdited ? { slug: conf.slug } : {}),
      ...(conf ? {
        titleFr: f.titleFr || conf.name,
        titleEn: f.titleEn || conf.nameEn || '',
        titleAr: f.titleAr || conf.nameAr || '',
        image: f.image || conf.image || '',
      } : {}),
    }));
  };

  const setPrice = (unit, value) => {
    setServiceForm((f) => ({ ...f, prices: { ...f.prices, [unit]: value } }));
  };

  const onImageFile = async (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png|webp)$/i.test(file.name)) {
      setMsg('Format non accepté — JPG, JPEG, PNG ou WEBP uniquement.');
      return;
    }
    setImgPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const res = await adminApi.uploadFile(file);
      setServiceForm((f) => ({ ...f, image: res.url }));
    } catch {
      setMsg("Échec de l'envoi de l'image — réessayez ou saisissez un chemin.");
    } finally {
      setUploading(false);
    }
  };

  const submitService = async (e) => {
    e.preventDefault();
    setMsg('');
    if (serviceForm.prestation === 'translation' && !serviceForm.tags.length) {
      setMsg('Sélectionnez au moins un type de traduction ou « Autre ».');
      return;
    }
    const requiredUnit = serviceForm.prestation === 'interpreting' ? 'hour' : 'document';
    const requiredLabel = serviceForm.prestation === 'interpreting' ? 'par heure' : 'par document';
    if (!serviceForm.quote && !(Number(serviceForm.prices?.[requiredUnit]) > 0)) {
      setMsg(`Le prix ${requiredLabel} est obligatoire (ou cochez « Prix à déterminer »).`);
      return;
    }
    const prices = {};
    if (!serviceForm.quote) {
      for (const [k, v] of Object.entries(serviceForm.prices || {})) {
        if (serviceForm.prestation === 'interpreting' ? k !== 'hour' : k === 'hour') continue;
        const n = Number(v);
        if (n > 0) prices[k] = n;
      }
    }
    const [firstUnit] = Object.keys(prices);
    const payload = {
      titleFr: serviceForm.titleFr,
      titleEn: serviceForm.titleEn,
      titleAr: serviceForm.titleAr,
      type: serviceForm.type || 'other',
      tags: serviceForm.tags,
      options: serviceForm.options,
      slug: serviceForm.slug || slugify(serviceForm.titleFr),
      description: serviceForm.description,
      image: serviceForm.image,
      isActive: Boolean(serviceForm.isActive),
      featured: Boolean(serviceForm.featured),
      category: serviceForm.prestation === 'interpreting' ? 'interpreting' : 'translation',
      prices,
      unit: firstUnit || '',
      basePrice: firstUnit ? prices[firstUnit] : 0,
      priceMax: 0,
    };
    try {
      if (editingService) {
        await adminApi.updateService(editingService.id, payload);
      } else {
        await adminApi.createService(payload);
      }
      closeForm();
      setMsg(editingService ? 'Service mis à jour avec succès.' : 'Service ajouté avec succès.');
      reload();
    } catch (err) {
      setMsg(err.data?.error || err.data?.message || 'Error');
    }
  };

  const editService = (s) => {
    const isInterp = s.category === 'interpreting' || s.type === 'interpreting';
    const interpConf = interpretingServices.find((i) => norm(i.slug) === norm(s.slug));
    setEditingService(s);
    setSlugEdited(true);
    setImgPreview('');
    setServiceForm({
      titleFr: s.titleFr || '', titleEn: s.titleEn || '', titleAr: s.titleAr || '',
      type: s.type || '',
      tags: Array.isArray(s.tags) && s.tags.length
        ? s.tags
        : (TRANSLATION_SLUGS.has(norm(s.type)) || DOCUMENT_SLUGS.has(norm(s.type)) ? [s.type] : []),
      options: Array.isArray(s.options) ? s.options : [],
      slug: s.slug || '', isActive: s.isActive !== false,
      featured: Boolean(s.featured),
      description: s.description || '', image: s.image || '',
      prices: pricesForForm(s),
      quote: !Object.values(pricesForForm(s)).some(Boolean),
      prestation: isInterp ? 'interpreting' : 'translation',
      interpChoice: isInterp ? (interpConf ? interpConf.slug : (norm(s.slug) === 'generalinterpreting' ? 'general' : 'other')) : '',
    });
    setFormOpen(true);
  };

  const openCreate = () => {
    setEditingService(null);
    setServiceForm(EMPTY_SERVICE);
    setSlugEdited(false);
    setImgPreview('');
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingService(null);
    setServiceForm(EMPTY_SERVICE);
    setSlugEdited(false);
    setImgPreview('');
    setUploading(false);
  };

  const toggleServiceActive = async (s) => {
    try {
      await adminApi.updateService(s.id, { isActive: !s.isActive });
      setMsg(s.isActive ? `« ${s.titleFr} » désactivé.` : `« ${s.titleFr} » activé.`);
      reload();
    } catch (err) {
      setMsg(err.data?.error || err.data?.message || 'Erreur');
    }
  };

  const removeService = async (s) => {
    try {
      await adminApi.deleteService(s.id);
      setMsg('Sous-service supprimé.');
      reload();
    } catch (err) {
      // 409 : utilisé dans des commandes → proposer la désactivation
      setMsg(err.data?.message || err.data?.error || 'Suppression impossible');
      reload();
    }
  };

  const confirmPending = async () => {
    const { kind, service } = confirmAction || {};
    setConfirmAction(null);
    if (!service) return;
    if (kind === 'delete') await removeService(service);
    else await toggleServiceActive(service);
  };

  return (
    <>
      <header className="dash-page-head dash-page-head--split">
        <div className="dash-page-head-text">
          <h1>Gestion des sous-services</h1>
          <p>Types de documents et éléments dynamiques rattachés aux services principaux (définis dans services.json).</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          <Plus size={15} aria-hidden="true" /> Ajouter un sous-service
        </button>
        <div className="cat-banner-stats">
          <div className="cat-banner-stat">
            <em><Layers size={13} aria-hidden="true" />Sous-services</em>
            <strong>{items.length}</strong>
            <small>{visibleServices.length} affichés</small>
          </div>
          <div className="cat-banner-stat">
            <em><CheckCircle2 size={13} aria-hidden="true" />Actifs</em>
            <strong>{items.filter((s) => s.isActive).length}</strong>
          </div>
          <div className="cat-banner-stat">
            <em><XCircle size={13} aria-hidden="true" />Inactifs</em>
            <strong>{items.filter((s) => !s.isActive).length}</strong>
          </div>
          <div className="cat-banner-stat">
            <em><FolderTree size={13} aria-hidden="true" />Services parents</em>
            <strong>{new Set(items.map((s) => s.type)).size}</strong>
          </div>
        </div>
      </header>
      {msg && (
        <div className="dash-toast dash-toast--float" role="status">
          <CheckCircle2 size={16} aria-hidden="true" />
          <span>{msg}</span>
          <button type="button" onClick={() => setMsg('')} aria-label="Fermer">
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="cat-toolbar">
        <div className="cat-tabs" role="tablist" aria-label="Type de prestation">
          {[
            { id: 'all', label: 'Toutes', count: items.length },
            { id: 'translation', label: 'Traduction', count: items.filter((s) => s.category !== 'interpreting' && s.type !== 'interpreting').length },
            { id: 'interpreting', label: 'Interprétariat', count: items.filter((s) => s.category === 'interpreting' || s.type === 'interpreting').length },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={prestation === tab.id}
              className={`cat-tab${prestation === tab.id ? ' is-active' : ''}`}
              onClick={() => { setPrestation(tab.id); setServiceFilter('all'); setDocFilter('all'); }}
            >
              {tab.label}
              <span className="cat-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      <DashModal
        open={formOpen}
        title={editingService ? `Modifier « ${editingService.titleFr} »` : 'Ajouter un sous-service'}
        subtitle="Sous-service rattaché à un service principal du services.json"
        onClose={closeForm}
        medium
      >
        <form className="dash-form svc-form" onSubmit={submitService}>
          {/* 1. Type de prestation */}
          <section className="dform-section">
            <header className="dform-section-head">
              <Layers size={17} aria-hidden="true" />
              <h4>1. Type de prestation</h4>
            </header>
            <div className="dform-field dform-wide">
              <div className="dform-radio-group">
                {[['translation', 'Traduction'], ['interpreting', 'Interprétariat']].map(([value, label]) => (
                  <label key={value} className="dform-radio">
                    <input
                      type="radio"
                      name="prestation"
                      checked={serviceForm.prestation === value}
                      onChange={() => onPrestationChange(value)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </section>

          {/* 2. Service principal */}
          <section className="dform-section">
            <header className="dform-section-head">
              <FolderTree size={17} aria-hidden="true" />
              <h4>2. Service principal</h4>
            </header>
            {serviceForm.prestation === 'interpreting' ? (
              <div className="dform-field dform-wide">
                <label>Service principal <span className="dform-req">*</span></label>
                <select required value={serviceForm.interpChoice} onChange={(e) => onServicePrincipalChange(e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  <option value="general">Général</option>
                  {interpretingServices.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                  <option value="other">Autre</option>
                </select>
              </div>
            ) : (
              <div className="dform-grid dform-grid--2">
                <div className="dform-field">
                  <label>Type de traduction <span className="dform-req">*</span></label>
                  <div className="dform-checkbox-scroll">
                    {TRANSLATION_TYPE_OPTIONS.map((s) => (
                      <label key={s.slug} className="dform-checkbox">
                        <input
                          type="checkbox"
                          checked={serviceForm.tags.some((t) => t.toLowerCase() === s.slug.toLowerCase())}
                          onChange={() => toggleTag(s.slug)}
                        />
                        {s.name}
                      </label>
                    ))}
                    <label className="dform-checkbox">
                      <input
                        type="checkbox"
                        checked={serviceForm.tags.includes('other')}
                        onChange={() => toggleTag('other')}
                      />
                      Autre
                    </label>
                  </div>
                </div>
                <div className="dform-field">
                  <label>Type de document</label>
                  <div className="dform-checkbox-scroll">
                    {DOCUMENT_TYPE_OPTIONS.map((s) => (
                      <label key={s.slug} className="dform-checkbox">
                        <input
                          type="checkbox"
                          checked={serviceForm.tags.some((t) => t.toLowerCase() === s.slug.toLowerCase())}
                          onChange={() => toggleTag(s.slug)}
                        />
                        {s.name}
                      </label>
                    ))}
                    <label className="dform-checkbox">
                      <input
                        type="checkbox"
                        checked={serviceForm.tags.includes('otherdoc')}
                        onChange={() => toggleTag('otherdoc')}
                      />
                      Autre
                    </label>
                  </div>
                </div>
                <p className="dform-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
                  Cochez un ou plusieurs types — le service apparaîtra sous chaque filtre coché.
                </p>
              </div>
            )}
          </section>

          {/* 3. Sous-service / Type de document */}
          <section className="dform-section">
            <header className="dform-section-head">
              <FileText size={17} aria-hidden="true" />
              <h4>3. Sous-service / Type de document</h4>
            </header>
            <div className="dform-grid dform-grid--2">
              <div className="dform-field">
                <label>Nom du sous-service <span className="dform-req">*</span></label>
                <input
                  required
                  value={serviceForm.titleFr}
                  onChange={(e) => {
                    const v = e.target.value;
                    setServiceForm((f) => ({ ...f, titleFr: v, ...(slugEdited ? {} : { slug: slugify(v) }) }));
                  }}
                  placeholder="ex. Acte de naissance"
                />
              </div>
              <div className="dform-field">
                <label>Nom (EN)</label>
                <input value={serviceForm.titleEn} onChange={(e) => setServiceForm({ ...serviceForm, titleEn: e.target.value })} />
              </div>
              <div className="dform-field">
                <label>Nom (AR)</label>
                <input value={serviceForm.titleAr} onChange={(e) => setServiceForm({ ...serviceForm, titleAr: e.target.value })} dir="rtl" />
              </div>
              <div className="dform-field">
                <label>Description</label>
                <input value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} />
              </div>
            </div>
          </section>

          {/* 4. Image */}
          <section className="dform-section">
            <header className="dform-section-head">
              <ImagePlus size={17} aria-hidden="true" />
              <h4>4. Image du service</h4>
            </header>
            <div
              className="svc-dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onImageFile(e.dataTransfer.files?.[0]); }}
            >
              <input
                id="svc-image-file"
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                hidden
                onChange={(e) => { onImageFile(e.target.files?.[0]); e.target.value = ''; }}
              />
              <label htmlFor="svc-image-file" className="svc-dropzone-label">
                <ImagePlus size={24} aria-hidden="true" />
                <span>{uploading ? 'Envoi en cours…' : 'Glissez-déposez une image ici ou cliquez pour sélectionner un fichier.'}</span>
                <small>Formats acceptés : JPG, JPEG, PNG, WEBP</small>
              </label>
            </div>
            {(imgPreview || serviceForm.image) && (
              <div className="svc-image-preview">
                <img
                  src={imgPreview || assetUrl(serviceForm.image)}
                  alt="Aperçu de l'image"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </section>

          {/* 5. Statut */}
          <section className="dform-section">
            <header className="dform-section-head">
              <CheckCircle2 size={17} aria-hidden="true" />
              <h4>5. Statut</h4>
            </header>
            <div className="dform-field dform-wide">
              <div className="dform-radio-group">
                <label className="dform-radio">
                  <input type="radio" name="svc-status" checked={serviceForm.isActive} onChange={() => setServiceForm({ ...serviceForm, isActive: true })} />
                  Actif
                </label>
                <label className="dform-radio">
                  <input type="radio" name="svc-status" checked={!serviceForm.isActive} onChange={() => setServiceForm({ ...serviceForm, isActive: false })} />
                  Inactif
                </label>
              </div>
            </div>
          </section>

          {/* 6. Tarification */}
          <section className="dform-section">
            <header className="dform-section-head">
              <Banknote size={17} aria-hidden="true" />
              <h4>6. Tarification</h4>
            </header>
            <div className="dform-grid dform-grid--2">
              {(serviceForm.prestation === 'interpreting'
                ? PRICE_FIELDS.filter(([u]) => u === 'hour')
                : PRICE_FIELDS.filter(([u]) => u !== 'hour')
              ).map(([unit, label]) => {
                const isRequired = unit === (serviceForm.prestation === 'interpreting' ? 'hour' : 'document');
                return (
                  <div className="dform-field" key={unit}>
                    <label>
                      {label}
                      {isRequired && !serviceForm.quote && <span className="dform-req"> *</span>}
                    </label>
                    <div className="svc-price">
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={serviceForm.prices[unit]}
                        required={isRequired && !serviceForm.quote}
                        disabled={serviceForm.quote}
                        onChange={(e) => setPrice(unit, e.target.value)}
                        placeholder="0"
                      />
                      <span>DT</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <label className="dform-check" style={{ maxWidth: 300 }}>
              <input
                type="checkbox"
                checked={serviceForm.quote}
                onChange={(e) => setServiceForm({ ...serviceForm, quote: e.target.checked })}
              />
              Prix à déterminer (sur devis)
            </label>
          </section>

          {/* 7. Options disponibles */}
          <section className="dform-section">
            <header className="dform-section-head">
              <ListChecks size={17} aria-hidden="true" />
              <h4>7. Options disponibles</h4>
            </header>
            <div className="dform-field dform-wide">
              <label>Options supplémentaires</label>
              <div className="dform-checkbox-scroll">
                {(serviceForm.prestation === 'interpreting' ? INTERPRETING_OPTIONS : TRANSLATION_OPTIONS).map(([slug, label]) => (
                  <label key={slug} className="dform-checkbox">
                    <input
                      type="checkbox"
                      checked={serviceForm.options.includes(slug)}
                      onChange={() => toggleOption(slug)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </section>

          {/* 7. Page d'accueil */}
          <section className="dform-section">
            <header className="dform-section-head">
              <CheckCircle2 size={17} aria-hidden="true" />
              <h4>7. Page d'accueil</h4>
            </header>
            <div className="dform-field dform-wide">
              <label className="dform-check" style={{ maxWidth: 420 }}>
                <input
                  type="checkbox"
                  checked={serviceForm.featured}
                  onChange={(e) => setServiceForm({ ...serviceForm, featured: e.target.checked })}
                />
                Afficher dans le carrousel de la page d'accueil
              </label>
            </div>
          </section>

          {/* 8. Enregistrement */}
          <footer className="dform-footer">
            <button type="button" className="btn btn-outline" onClick={closeForm}>
              {d.cancel || 'Annuler'}
            </button>
            <button type="submit" className="btn btn-primary">Enregistrer le service</button>
          </footer>
        </form>
      </DashModal>

      {/* Confirmation activer / désactiver / supprimer */}
      <DashModal
        open={Boolean(confirmAction)}
        title={
          confirmAction?.kind === 'delete'
            ? 'Supprimer le service'
            : confirmAction?.service?.isActive
              ? 'Désactiver le service'
              : 'Activer le service'
        }
        onClose={() => setConfirmAction(null)}
      >
        {confirmAction && (
          <div className="svc-confirm">
            <p>
              {confirmAction.kind === 'delete'
                ? `Voulez-vous vraiment supprimer « ${confirmAction.service.titleFr} » ? Cette action est définitive.`
                : confirmAction.service.isActive
                  ? `Voulez-vous désactiver « ${confirmAction.service.titleFr} » ? Il ne sera plus visible sur le site.`
                  : `Voulez-vous activer « ${confirmAction.service.titleFr} » ? Il sera visible sur le site.`}
            </p>
            <div className="svc-confirm-actions">
              <button type="button" className="btn btn-outline" onClick={() => setConfirmAction(null)}>
                Annuler
              </button>
              <button
                type="button"
                className={`btn ${confirmAction.kind === 'delete' ? 'btn-danger' : 'btn-primary'}`}
                onClick={confirmPending}
              >
                Confirmer
              </button>
            </div>
          </div>
        )}
      </DashModal>

      <section className="dash-panel" style={{ marginTop: 16 }}>
        <div className="cat-filterbar">
          <div className="cat-search">
            <Search size={15} aria-hidden="true" />
            <input
              placeholder="Rechercher (nom, slug)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Rechercher un sous-service"
            />
          </div>
          {prestation !== 'all' && (
            <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} aria-label="Service">
              <option value="all">Service — Tous</option>
              {(prestation === 'interpreting'
                ? interpretingServices.map((s) => ({ slug: s.slug, name: s.name }))
                : mainServiceGroups.filter((g) => g.id !== 'documents').flatMap((g) => g.services.map((s) => ({ slug: s.slug, name: s.name })))
              ).map((o) => <option key={o.slug} value={o.slug}>{o.name}</option>)}
              <option value="other">Autre</option>
            </select>
          )}
          {prestation === 'translation' && (
            <select value={docFilter} onChange={(e) => setDocFilter(e.target.value)} aria-label="Type de document">
              <option value="all">Type de document — Tous</option>
              {(mainServiceGroups.find((g) => g.id === 'documents')?.services || []).map((s) => (
                <option key={s.slug} value={s.slug}>{s.name}</option>
              ))}
              <option value="other">Autre (etc.)</option>
            </select>
          )}
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filtrer par statut">
            <option value="all">Tous statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>
          {(search || serviceFilter !== 'all' || docFilter !== 'all' || statusFilter !== 'all' || prestation !== 'all') && (
            <button
              type="button"
              className="cat-reset"
              onClick={() => { setSearch(''); setPrestation('all'); setServiceFilter('all'); setDocFilter('all'); setStatusFilter('all'); }}
            >
              <RotateCcw size={13} aria-hidden="true" /> Réinitialiser
            </button>
          )}
          <span className="cat-count">{visibleServices.length} / {items.length}</span>
        </div>
        {visibleServices.length === 0 ? (
          <p style={{ textAlign: 'center', opacity: 0.6, padding: '24px 0' }}>Aucun sous-service</p>
        ) : (
          <div className="all-services-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
            {pg.pageItems.map((s) => {
              const entries = priceEntries(s);
              const reviewCount = s.rating || 0;
              return (
                <div key={s.id} className={`all-services-card admin-svc-card${s.isActive ? '' : ' is-inactive'}`}>
                  <div className="all-services-card-media">
                    {s.image && (
                      <img
                        src={assetUrl(s.image)}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <span className="admin-svc-status">
                      <StatusBadge status={s.isActive ? 'active' : 'cancelled'} labels={{ active: 'Actif', cancelled: 'Inactif' }} />
                    </span>
                  </div>
                  <div className="all-services-card-body">
                    <h3>{s.titleFr}</h3>
                    <div className="admin-svc-meta">
                      <code>{s.slug}</code> · {parentLabel(s.type)}
                    </div>
                    <div className="all-services-card-rating">
                      <div className="all-services-card-stars" aria-label={`${reviewCount} avis`}>
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={14} fill={reviewCount > 0 ? 'currentColor' : 'none'} aria-hidden="true" />
                        ))}
                      </div>
                      <span className="all-services-card-reviews">
                        {reviewCount} avis{s.orders ? ` · ${s.orders} cmd` : ''}
                      </span>
                    </div>
                    <p className="all-services-card-price">
                      {entries.length ? (
                        <>
                          {entries.map(([u, p]) => (
                            <strong key={u || 'fixed'}>{p} TND{UNIT_LABELS[u] || (u ? `/${u}` : '')}</strong>
                          ))}
                          <span className="all-services-card-price-separator">/</span>
                          <span className="all-services-card-tnd">
                            {formatEur(tndToEur(Math.min(...entries.map(([, p]) => Number(p)))), locale)}
                          </span>
                        </>
                      ) : (
                        <span className="all-services-card-from">Sur devis</span>
                      )}
                    </p>
                    <div className="admin-svc-actions translator-card__actions">
                      <button
                        type="button"
                        className="translator-card__btn"
                        onClick={() => editService(s)}
                      >
                        <Pencil size={14} aria-hidden="true" /> {d.edit || 'Modifier'}
                      </button>
                      <button
                        type="button"
                        className={`translator-card__btn ${s.isActive ? 'translator-card__btn--danger' : 'translator-card__btn--success'}`}
                        onClick={() => setConfirmAction({ kind: 'toggle', service: s })}
                      >
                        <Power size={14} aria-hidden="true" /> {s.isActive ? a.deactivate : a.activate}
                      </button>
                      <button
                        type="button"
                        className="translator-card__btn translator-card__btn--danger"
                        onClick={() => setConfirmAction({ kind: 'delete', service: s })}
                      >
                        <Trash2 size={14} aria-hidden="true" /> {a.delete}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pagination page={pg.page} totalPages={pg.totalPages} total={pg.total} onChange={pg.setPage} />
      </section>
    </>
  );
}

export function AdminInvoicesPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderId, setOrderId] = useState('');
  const invoicesPg = usePagination(invoices, 6);
  const [msg, setMsg] = useState('');

  const reload = () => {
    adminApi.invoices().then((res) => setInvoices(res.invoices || [])).catch(() => setInvoices([]));
    adminApi.orders().then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));
  };
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createInvoice({ orderId });
      setOrderId('');
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.invoicesTitle}</h1>
        <p>{a.invoicesSubtitle}</p>
      </header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        <form className="dash-form" onSubmit={create} style={{ maxWidth: 420 }}>
          <div className="dash-field">
            <label>{a.ordersTitle}</label>
            <select required value={orderId} onChange={(e) => setOrderId(e.target.value)}>
              <option value="">—</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>{o.id} · {o.document} · {o.client}</option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn btn-primary">{a.createInvoice}</button>
        </form>
      </section>
      <section className="dash-panel" style={{ marginTop: 16 }}>
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{a.colClient}</th>
                <th>{a.colTotal}</th>
                <th>{a.colStatus}</th>
                <th>{d.actions}</th>
              </tr>
            </thead>
            <tbody>
              {invoicesPg.pageItems.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.id}</td>
                  <td>{inv.client || '—'}</td>
                  <td>{inv.amount} {inv.currency}</td>
                  <td><StatusBadge status={inv.status} labels={d.status} /></td>
                  <td>
                    {inv.status !== 'paid' && (
                      <button type="button" className="dash-btn-sm" onClick={async () => { await adminApi.updateInvoice(inv.id, { status: 'paid' }); reload(); }}>
                        {a.markPaid}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={invoicesPg.page} totalPages={invoicesPg.totalPages} total={invoicesPg.total} onChange={invoicesPg.setPage} />
      </section>
    </>
  );
}

const ROLE_LABELS = { client: 'Client', translator: 'Pro', lawyer: 'Avocat', notary: 'Notaire' };

export function AdminMessagesPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [toId, setToId] = useState('');
  const [body, setBody] = useState('');
  const [threadKey, setThreadKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const threads = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      if (!map.has(m.threadKey)) map.set(m.threadKey, { key: m.threadKey, people: new Map(), last: m.createdAt, preview: '' });
      const thread = map.get(m.threadKey);
      [m.from, m.to].forEach((p) => {
        if (p && p.role !== 'admin') thread.people.set(p.id, p);
      });
      if (new Date(m.createdAt) >= new Date(thread.last || 0)) {
        thread.last = m.createdAt;
        thread.preview = m.body;
      }
    });
    return Array.from(map.values())
      .sort((x, y) => new Date(y.last || 0) - new Date(x.last || 0))
      .map((thread) => ({
        ...thread,
        name: Array.from(thread.people.values()).map((p) => p.name).filter(Boolean).join(' ↔ ') || thread.key,
      }));
  }, [messages]);

  const reload = () => {
    adminApi.messages().then((res) => setMessages(res.messages || [])).catch(() => setMessages([]));
  };

  useEffect(() => {
    reload();
    adminApi.users().then((res) => setUsers((res.users || []).filter((u) => u.role !== 'admin'))).catch(() => setUsers([]));
    const timer = setInterval(reload, 4000);
    return () => clearInterval(timer);
  }, []);

  const pickThread = (key) => {
    setThreadKey(key);
    if (key === 'new' || !key) {
      setToId('');
      return;
    }
    const last = [...messages].reverse().find((m) => m.threadKey === key && m.from?.role !== 'admin');
    const thread = threads.find((x) => x.key === key);
    setToId(last?.from?.id || Array.from(thread?.people.keys() || [])[0] || '');
  };

  const filtered = threadKey && threadKey !== 'new' ? messages.filter((m) => m.threadKey === threadKey) : [];

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !toId) return;
    setBusy(true);
    setMsg('');
    try {
      const existing = threadKey && threadKey !== 'new';
      await adminApi.sendMessage({ toId, body, ...(existing ? { threadKey } : {}) });
      const recipient = users.find((u) => u.id === toId);
      const key = existing
        ? threadKey
        : `${{ client: 'client', translator: 'pro', lawyer: 'verifier', notary: 'verifier' }[recipient?.role] || 'pro'}:${toId}`;
      setBody('');
      reload();
      setThreadKey(key);
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.messagesTitle}</h1>
        <p>{a.messagesSubtitle}</p>
      </header>
      {msg && <p className="dash-toast">{msg}</p>}
      <MessengerPanel
        threads={threads}
        activeKey={threadKey}
        onSelect={pickThread}
        messages={filtered}
        myRole="admin"
        body={body}
        setBody={setBody}
        onSend={send}
        busy={busy}
        labels={{
          conversations: 'Conversations',
          newMessage: 'Nouveau message',
          selectConversation: 'Sélectionnez une conversation',
          writeMessage: 'Écrire un message…',
          empty: d.empty,
          send: a.sendMessage,
        }}
        contacts={users.map((u) => ({ key: u.id, name: u.name, sub: ROLE_LABELS[u.role] || u.role }))}
        contactKey={toId}
        onPickContact={setToId}
        contactsTitle={a.recipient}
        requireContact
      />
    </>
  );
}

const PAYMENT_METHOD_LABELS = {
  card: 'Carte bancaire',
  transfer: 'Virement',
  virement: 'Virement',
  cash: 'Espèces',
  paypal: 'PayPal',
  other: 'Autre',
};

const methodLabel = (m) => PAYMENT_METHOD_LABELS[m] || m || '—';

export function AdminPaymentsPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    adminApi.payments().then((res) => setPayments(res.payments || [])).catch(() => setPayments([]));
  }, []);

  const sum = (rows) => rows.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const stats = [
    { icon: Banknote, label: 'Total payé', value: sum(payments.filter((p) => p.status === 'paid')) },
    { icon: Clock, label: 'En attente', value: sum(payments.filter((p) => p.status === 'pending')) },
    { icon: RotateCcw, label: 'Remboursé', value: sum(payments.filter((p) => p.status === 'refunded')) },
  ];

  const filtered = payments.filter((p) => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (methodFilter !== 'all' && (p.method || 'card') !== methodFilter) return false;
    const at = p.paidAt || p.createdAt || '';
    if (dateFrom && at < dateFrom) return false;
    if (dateTo && at > dateTo) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [p.id, p.orderCode, p.quoteCode, p.client, p.clientEmail, p.reference]
      .some((x) => String(x || '').toLowerCase().includes(q));
  });
  const paymentsPg = usePagination(filtered, 12);

  const payDate = (p) => p.paidAt || p.createdAt || '—';

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.paymentsTitle || 'Paiements'}</h1>
        <p>{a.paymentsSubtitle || 'Suivre les paiements, remboursements et transactions des commandes.'}</p>
      </header>

      <div className="cp-stats">
        {stats.map((k) => (
          <div className="dash-stat cp-stat" key={k.label}>
            <span className="cp-stat-icon"><k.icon size={15} /></span>
            <span>{k.label}</span>
            <strong>{k.value} TND</strong>
          </div>
        ))}
      </div>

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />Filtres</span>
          <span className="of-meta">
            <button
              type="button"
              className="of-reset"
              onClick={() => { setSearch(''); setStatusFilter('all'); setMethodFilter('all'); setDateFrom(''); setDateTo(''); }}
            >
              <RotateCcw size={13} />Réinitialiser
            </button>
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>Recherche</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une transaction…"
            />
          </div>
          <div className="of-field">
            <label>{d.statusLabel || 'Statut'}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous</option>
              <option value="pending">{d.payment?.pending || 'En attente'}</option>
              <option value="paid">{d.payment?.paid || 'Payé'}</option>
              <option value="failed">{d.payment?.failed || 'Échoué'}</option>
              <option value="refunded">{d.payment?.refunded || 'Remboursé'}</option>
            </select>
          </div>
          <div className="of-field">
            <label>Méthode</label>
            <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
              <option value="all">Toutes</option>
              <option value="card">Carte bancaire</option>
              <option value="transfer">Virement</option>
              <option value="other">Autre</option>
            </select>
          </div>
          <div className="of-field">
            <label>Du</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="of-field">
            <label>Au</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="orders-panel-head">
        <h2>{a.paymentsTitle || 'Paiements'}</h2>
        <span className="orders-panel-count">{filtered.length}</span>
      </div>

      <section className="dash-panel">
        {paymentsPg.pageItems.length === 0 ? (
          <p className="dash-empty">{d.empty}</p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Transaction</th>
                  <th>Commande</th>
                  <th>{a.colClient}</th>
                  <th>Montant</th>
                  <th>Méthode</th>
                  <th>{a.colStatus}</th>
                  <th>Date</th>
                  <th>{d.actions}</th>
                </tr>
              </thead>
              <tbody>
                {paymentsPg.pageItems.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{p.orderCode || p.quoteCode || '—'}</td>
                    <td>{p.client || '—'}</td>
                    <td><strong>{p.amount} {p.currency || 'TND'}</strong></td>
                    <td>{methodLabel(p.method)}</td>
                    <td><StatusBadge status={p.status} labels={d.payment} /></td>
                    <td>{payDate(p)}</td>
                    <td>
                      <button type="button" className="dash-btn-sm" onClick={() => setDetail(p)}>
                        <Eye size={13} /> {d.view || 'Voir'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={paymentsPg.page} totalPages={paymentsPg.totalPages} total={paymentsPg.total} onChange={paymentsPg.setPage} />
      </section>

      <DashModal
        open={Boolean(detail)}
        title={detail ? `Paiement ${detail.id}` : ''}
        subtitle={detail?.orderCode || detail?.quoteCode || ''}
        onClose={() => setDetail(null)}
      >
        {detail && (
          <div style={{ padding: '4px 2px' }}>
            <dl className="od-dl" style={{ marginBottom: 16 }}>
              <div className="od-row"><span className="od-label">Commande</span><span className="od-value">{detail.orderCode || detail.quoteCode || '—'}</span></div>
              <div className="od-row"><span className="od-label">Client</span><span className="od-value">{detail.client || '—'}{detail.clientEmail ? ` · ${detail.clientEmail}` : ''}</span></div>
              <div className="od-row"><span className="od-label">Montant</span><span className="od-value"><strong>{detail.amount} {detail.currency || 'TND'}</strong></span></div>
              <div className="od-row"><span className="od-label">Méthode</span><span className="od-value">{methodLabel(detail.method)}</span></div>
              <div className="od-row"><span className="od-label">Statut</span><span className="od-value"><StatusBadge status={detail.status} labels={d.payment} /></span></div>
              <div className="od-row"><span className="od-label">Référence</span><span className="od-value">{detail.reference || '—'}</span></div>
              <div className="od-row"><span className="od-label">Date</span><span className="od-value">{payDate(detail)}</span></div>
            </dl>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-outline" onClick={() => setDetail(null)}>Fermer</button>
              {detail.orderCode && (
                <Link className="btn btn-primary" to={`/admin/orders/${detail.orderCode}`} onClick={() => setDetail(null)}>
                  Voir la commande <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </div>
        )}
      </DashModal>
    </>
  );
}

export function AdminNotificationsPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const reload = () => adminApi.notifications().then((res) => setItems(res.notifications || [])).catch(() => setItems([]));
  useEffect(() => {
    reload();
    const timer = setInterval(reload, 10000);
    return () => clearInterval(timer);
  }, []);

  const openNotif = (n) => {
    if (!n.readAt) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      adminApi.readNotifications({ id: n.id }).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await adminApi.readNotifications();
      setItems((prev) => prev.map((x) => ({ ...x, readAt: x.readAt || new Date().toISOString() })));
    } catch { /* ignore */ }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.notificationsTitle || 'Notifications'}</h1>
        <p>{a.notificationsSubtitle || 'Alertes administrateur.'}</p>
      </header>
      <NotificationsPanel
        items={items}
        onOpen={openNotif}
        onMarkAll={markAll}
        labels={{ all: 'Toutes', unread: 'Non lues', markAllRead: a.markAllRead || 'Tout marquer comme lu', empty: d.empty }}
      />
    </>
  );
}
